/**
 * How to open an Airwallex hosted checkout whose URL only arrives after an
 * async API call.
 *
 * Desktop browsers get a popup pre-opened synchronously in the click handler
 * (so popup blockers allow it) which is then pointed at the checkout, keeping
 * the originating page and its modal state alive.
 *
 * Some mobile browsers switch to the new tab at once and suspend the opener,
 * so the API call never completes and the new tab is never pointed at the
 * checkout. On touch devices the checkout therefore opens in the current tab
 * and the app relies on the checkout's return URL (`?payment_updated=true`)
 * plus a `sessionStorage` resume record to pick up where it left off.
 */
export function prefersSameTabCheckout(): boolean {
  if (typeof window === 'undefined') return false;
  const coarsePointer =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  const mobileUserAgent = /iPhone|iPad|iPod|Android/i.test(
    window.navigator.userAgent,
  );
  return coarsePointer || mobileUserAgent;
}

/**
 * Pre-open a blank popup for a hosted checkout. Returns null when the checkout
 * should be opened in the current tab instead (see `prefersSameTabCheckout`),
 * or when the browser blocked the popup.
 */
export function openCheckoutPopup(): Window | null {
  if (prefersSameTabCheckout()) return null;
  return window.open('about:blank', '_blank');
}

export function navigateToCheckout(checkoutUrl: string): void {
  window.location.assign(checkoutUrl);
}

export const PAYMENT_UPDATED_PARAM = 'payment_updated';

/**
 * State a host stashes right before a same-tab checkout so it can rebuild the
 * UI the user left (the page is unloaded in between). `host` names the
 * component that owns the state; `key` narrows it further when several
 * instances of that host can be mounted on one page (e.g. a thread id).
 */
export interface CheckoutResume<T = unknown> {
  host: string;
  key?: string | null;
  state: T;
}

export interface TakenCheckoutResume<T> {
  state: T;
  /** True when the return URL carries `payment_updated=true` (card saved). */
  cardAdded: boolean;
}

interface StoredCheckoutResume extends CheckoutResume {
  path: string;
  savedAt: number;
}

export const CHECKOUT_RESUME_STORAGE_KEY = 'dream.hostedCheckoutResume';
const CHECKOUT_RESUME_TTL_MS = 30 * 60 * 1000;

function currentPathWithoutMarker(): string {
  const params = new URLSearchParams(window.location.search);
  params.delete(PAYMENT_UPDATED_PARAM);
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ''}`;
}

function readStoredResume(): StoredCheckoutResume | null {
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_RESUME_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as StoredCheckoutResume).host !== 'string' ||
      typeof (parsed as StoredCheckoutResume).path !== 'string' ||
      typeof (parsed as StoredCheckoutResume).savedAt !== 'number'
    ) {
      return null;
    }
    return parsed as StoredCheckoutResume;
  } catch {
    return null;
  }
}

export function stashCheckoutResume(resume: CheckoutResume): void {
  if (typeof window === 'undefined') return;
  const record: StoredCheckoutResume = {
    ...resume,
    path: currentPathWithoutMarker(),
    savedAt: Date.now(),
  };
  try {
    window.sessionStorage.setItem(
      CHECKOUT_RESUME_STORAGE_KEY,
      JSON.stringify(record),
    );
  } catch {
    // Storage full or disabled — the checkout still works, only the resume is lost.
  }
}

/**
 * Hand a stashed record back to the host that owns it, once. Only matches a
 * record stashed on this same page (path) by this `host`/`key`, and drops
 * records older than the TTL.
 */
export function takeCheckoutResume<T = unknown>(
  host: string,
  key?: string | null,
): TakenCheckoutResume<T> | null {
  if (typeof window === 'undefined') return null;
  const record = readStoredResume();
  if (!record) return null;
  if (Date.now() - record.savedAt > CHECKOUT_RESUME_TTL_MS) {
    window.sessionStorage.removeItem(CHECKOUT_RESUME_STORAGE_KEY);
    return null;
  }
  if (record.host !== host) return null;
  if ((record.key ?? null) !== (key ?? null)) return null;
  if (record.path !== currentPathWithoutMarker()) return null;
  window.sessionStorage.removeItem(CHECKOUT_RESUME_STORAGE_KEY);
  return {
    state: record.state as T,
    cardAdded:
      new URLSearchParams(window.location.search).get(PAYMENT_UPDATED_PARAM) ===
      'true',
  };
}
